import PostalMime from 'postal-mime';
import type { Address, Attachment, Email, Mailbox } from 'postal-mime';

export type EmailItemStatus = 'ready' | 'error' | 'converting' | 'converted';

export interface NormalizedAttachment {
  id: string;
  filename: string;
  mimeType: string;
  size: number;
  content: Uint8Array;
  disposition: 'attachment' | 'inline' | null;
  contentId?: string;
  related?: boolean;
  embeddableInPdf: boolean;
  unsupportedReason?: string;
}

export interface NormalizedEmail {
  id: string;
  sourceFileName: string;
  status: EmailItemStatus;
  errorMessage?: string;
  /** When true, the file could not be parsed and should not be sent to conversion */
  isParseError?: boolean;
  subject: string;
  from: string;
  to: string;
  cc: string;
  date: string;
  textBody: string | null;
  htmlBody: string | null;
  attachments: NormalizedAttachment[];
}

function formatMailbox(m: Mailbox): string {
  const name = (m.name || '').trim();
  const addr = (m.address || '').trim();
  if (name && addr) return `${name} <${addr}>`;
  return addr || name || '—';
}

function formatSingleAddress(addr?: Address | null): string {
  if (!addr) return '—';
  if ('group' in addr && addr.group?.length) {
    return addr.group.map(formatMailbox).join(', ');
  }
  return formatMailbox(addr as Mailbox);
}

function formatAddressList(list?: Address[] | null): string {
  if (!list?.length) return '—';
  return list.map((a) => formatSingleAddress(a)).join(', ');
}

function toUint8Array(content: Attachment['content']): Uint8Array {
  if (content instanceof Uint8Array) return content;
  if (content instanceof ArrayBuffer) return new Uint8Array(content);
  if (typeof content === 'string') return new TextEncoder().encode(content);
  return new Uint8Array();
}

function extension(filename: string): string {
  const i = filename.lastIndexOf('.');
  if (i === -1) return '';
  return filename.slice(i + 1).toLowerCase();
}

export function classifyAttachment(filename: string, mimeType: string): { embeddable: boolean; reason?: string } {
  const mime = (mimeType || '').toLowerCase();
  const ext = extension(filename);

  if (mime === 'application/pdf' || ext === 'pdf') {
    return { embeddable: true };
  }

  if (mime.startsWith('image/')) {
    if (mime.includes('jpeg') || mime.includes('jpg') || mime.includes('png') || mime.includes('webp')) {
      return { embeddable: true };
    }
    return { embeddable: false, reason: 'Image format not supported for embedding' };
  }

  if (['jpg', 'jpeg', 'png', 'webp'].includes(ext)) {
    return { embeddable: true };
  }
  if (['gif', 'bmp', 'svg'].includes(ext)) {
    return { embeddable: false, reason: 'Image format not supported for embedding' };
  }

  if (
    mime === 'text/plain' ||
    mime === 'text/csv' ||
    mime === 'application/csv' ||
    ext === 'txt' ||
    ext === 'csv'
  ) {
    return { embeddable: true };
  }

  return {
    embeddable: false,
    reason: 'Not converted — listed in the attachment section only',
  };
}

function normalizeAttachments(emailId: string, raw: Attachment[]): NormalizedAttachment[] {
  return raw.map((att, index) => {
    const filename = att.filename?.trim() || `attachment-${index + 1}`;
    const bytes = toUint8Array(att.content);
    const { embeddable, reason } = classifyAttachment(filename, att.mimeType || '');

    return {
      id: `${emailId}-att-${index}`,
      filename,
      mimeType: att.mimeType || 'application/octet-stream',
      size: bytes.byteLength,
      content: bytes,
      disposition: att.disposition,
      contentId: att.contentId?.replace(/^<|>$/g, ''),
      related: att.related,
      embeddableInPdf: embeddable,
      unsupportedReason: embeddable ? undefined : reason,
    };
  });
}

export async function parseEml(file: File, emailId: string): Promise<NormalizedEmail> {
  const raw = await file.arrayBuffer();
  let parsed: Email;
  try {
    parsed = await PostalMime.parse(raw, { attachmentEncoding: 'arraybuffer' });
  } catch (e) {
    const message = e instanceof Error ? e.message : 'Invalid or corrupted .eml file';
    return {
      id: emailId,
      sourceFileName: file.name,
      status: 'error',
      isParseError: true,
      errorMessage: message,
      subject: '—',
      from: '—',
      to: '—',
      cc: '—',
      date: '—',
      textBody: null,
      htmlBody: null,
      attachments: [],
    };
  }

  return {
    id: emailId,
    sourceFileName: file.name,
    status: 'ready',
    isParseError: false,
    subject: parsed.subject?.trim() || '(No subject)',
    from: formatSingleAddress(parsed.from),
    to: formatAddressList(parsed.to),
    cc: formatAddressList(parsed.cc),
    date: parsed.date?.trim() || '—',
    textBody: parsed.text ?? null,
    htmlBody: parsed.html ?? null,
    attachments: normalizeAttachments(emailId, parsed.attachments || []),
  };
}
